import { useState, useMemo, useEffect } from 'react';
import { useQueryClient, InfiniteData } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { MediaLibraryItem, MediaLibraryFilters, MediaType } from '@/types/media-library';
import { MediaItem } from '@/types/posts';
import { useMediaLibraryInfinite } from '@/hooks/useMediaLibrary';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { CancelButton } from '@/components/ui/CancelButton';
import { RetryImage } from '@/components/ui';
import { MediaUploadZone } from '@/components/composer/MediaUploadZone';
import { cn } from '@/utils/cn';
import { ICONS } from '@/constants/icons';
import { useAuth } from '@/hooks/useAuth';
import { getEffectiveTeamId, teamQueryKeys } from '@/lib/queryKeys';
import { mediaLibraryService } from '@/services/media-library.service';
import { devLog } from '@/utils/logger';

/**
 * Type for the page structure returned by the infinite query
 */
interface MediaLibraryPage {
  items: MediaLibraryItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/**
 * MediaPicker Component
 *
 * Reusable modal for browsing and selecting media from the library
 *
 * Features:
 * - Search with debounce (300ms)
 * - Filter by media type (IMAGE/VIDEO)
 * - Infinite scroll
 * - Single/multi selection mode
 * - Upload integration
 * - Responsive grid (2/3/4 columns)
 */

export interface MediaPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (media: MediaLibraryItem[]) => void;
  mode?: 'single' | 'multi';
  maxSelection?: number;
  selectedIds?: number[];
  allowedTypes?: MediaType[];
  title?: string;
  /** Set to true when opened from inside another modal/drawer */
  nested?: boolean;
}

export const MediaPicker = ({
  isOpen,
  onClose,
  onSelect,
  mode = 'multi',
  maxSelection,
  selectedIds = [],
  allowedTypes,
  title = 'Select Media',
  nested = false,
}: MediaPickerProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const teamId = getEffectiveTeamId(user);

  // State
  const [selectedItems, setSelectedItems] = useState<Map<number, MediaLibraryItem>>(new Map());
  const [typeFilter, setTypeFilter] = useState<MediaType | undefined>();
  const [showUpload, setShowUpload] = useState(false);

  // Filters
  const filters: MediaLibraryFilters = useMemo(() => ({
    type: typeFilter,
    isArchived: false,
  }), [typeFilter]);

  // Data fetching with infinite scroll
  const infiniteQuery = useMediaLibraryInfinite(filters, 24);
  const { fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = infiniteQuery;

  // Flatten paginated data
  const allMedia = useMemo(() => {
    // Cast to InfiniteData structure (TanStack Query wraps data in InfiniteData<T>)
    const infiniteData = infiniteQuery.data as InfiniteData<MediaLibraryPage> | undefined;
    if (!infiniteData?.pages) return [];
    return infiniteData.pages.flatMap((page: MediaLibraryPage) => page.items);
  }, [infiniteQuery.data]);

  // Initialize selected items from selectedIds prop
  useEffect(() => {
    if (selectedIds.length > 0 && allMedia.length > 0) {
      const initialSelected = new Map<number, MediaLibraryItem>();
      selectedIds.forEach((id: number) => {
        const item = allMedia.find((m: MediaLibraryItem) => m.id === id);
        if (item) {
          initialSelected.set(id, item);
        }
      });
      setSelectedItems(initialSelected);
    }
  }, [selectedIds, allMedia]);

  // Selection handlers
  const handleItemClick = (item: MediaLibraryItem) => {
    if (mode === 'single') {
      // Single mode: replace selection
      const newSelection = new Map<number, MediaLibraryItem>();
      newSelection.set(item.id, item);
      setSelectedItems(newSelection);
    } else {
      // Multi mode: toggle selection
      const newSelection = new Map(selectedItems);
      if (newSelection.has(item.id)) {
        newSelection.delete(item.id);
      } else {
        // Check max selection limit
        if (maxSelection && newSelection.size >= maxSelection) {
          alert(`Maximum ${maxSelection} items can be selected`);
          return;
        }
        newSelection.set(item.id, item);
      }
      setSelectedItems(newSelection);
    }
  };

  // Upload complete handler - promotes to library first, then selects
  const handleUploadComplete = async (media: MediaItem[]) => {
    try {
      // Promote each uploaded file to the media library (same as MediaLibrary page)
      const promotedItems = await Promise.all(
        media.map((item) => mediaLibraryService.promote(item.id))
      );

      // Refresh media library cache
      if (teamId) {
        queryClient.invalidateQueries({ queryKey: teamQueryKeys.mediaLibrary.all(teamId) });
        queryClient.invalidateQueries({ queryKey: teamQueryKeys.mediaLibrary.stats(teamId) });
      }

      // Select the promoted library items for the post
      onSelect(promotedItems);
      onClose();
    } catch (error) {
      devLog.error('Failed to promote media to library:', error);
      // Fallback: still try to select with converted items
      const fallbackItems: MediaLibraryItem[] = media.map((item) => ({
        id: Number(item.id),
        url: item.url,
        thumbnailUrl: item.thumbnail,
        type: item.type.toUpperCase() as MediaType,
        filename: item.filename || 'upload',
        fileSize: item.size || 0,
        width: item.width,
        height: item.height,
        duration: item.duration,
        createdAt: new Date().toISOString(),
      }));
      onSelect(fallbackItems);
      onClose();
    }
  };

  // Confirm selection
  const handleConfirm = () => {
    const selected = Array.from(selectedItems.values());
    onSelect(selected);
    onClose();
  };

  // Clear filters
  const handleClearFilters = () => {
    setTypeFilter(undefined);
  };

  // Filter media by allowed types
  const filteredMedia = useMemo(() => {
    if (!allowedTypes || allowedTypes.length === 0) {
      return allMedia;
    }
    return allMedia.filter((item: MediaLibraryItem) => allowedTypes.includes(item.type));
  }, [allMedia, allowedTypes]);

  // Empty state check
  const hasFilters = !!typeFilter;
  const isEmpty = filteredMedia.length === 0 && !isLoading;

  return (
    <Modal
      open={isOpen}
      onOpenChange={onClose}
      title={title}
      size="lg"
      nested={nested}
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            {selectedItems.size > 0 && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-full text-sm font-medium">
                <i className={`hm hm-${ICONS.CHECK_CIRCLE} text-xs`} />
                {t('mediapicker_selected', { count: selectedItems.size })}
              </span>
            )}
          </div>
          <div className="flex gap-1.5 sm:gap-2">
            <CancelButton onClick={onClose} tooltip="Cancel" />
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={selectedItems.size === 0}
            >
              {mode === 'single' ? t('select') : selectedItems.size === 0 ? t('add') : t('mediapicker_add', { count: selectedItems.size })}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4 lg:space-y-4">
        {/* Tabs for Library/Upload */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg">
          <button
            onClick={() => setShowUpload(false)}
            className={cn(
              'flex-1 px-3 lg:px-4 py-1.5 lg:py-2 text-xs lg:text-sm font-medium rounded-md transition-all',
              !showUpload
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            )}
          >
            <i className={`hm hm-${ICONS.IMAGE} mr-1.5 lg:mr-2`} />
            {t('mediapicker_library')}
          </button>
          <button
            onClick={() => setShowUpload(true)}
            className={cn(
              'flex-1 px-3 lg:px-4 py-1.5 lg:py-2 text-xs lg:text-sm font-medium rounded-md transition-all',
              showUpload
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            )}
          >
            <i className={`hm hm-${ICONS.UPLOAD} mr-1.5 lg:mr-2`} />
            {t('mediapicker_upload')}
          </button>
        </div>

        {/* Type Filter Pills - only show when in Library view */}
        {!showUpload && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setTypeFilter(undefined)}
              className={cn(
                'px-2 lg:px-3 py-1 lg:py-1.5 rounded-full text-xs lg:text-sm font-medium transition-colors whitespace-nowrap',
                !typeFilter
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              {t('mediapicker_all')}
            </button>
            <button
              onClick={() => setTypeFilter('IMAGE')}
              className={cn(
                'px-2 lg:px-3 py-1 lg:py-1.5 rounded-full text-xs lg:text-sm font-medium transition-colors whitespace-nowrap',
                typeFilter === 'IMAGE'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              {t('mediapicker_images')}
            </button>
            <button
              onClick={() => setTypeFilter('VIDEO')}
              className={cn(
                'px-2 lg:px-3 py-1 lg:py-1.5 rounded-full text-xs lg:text-sm font-medium transition-colors whitespace-nowrap',
                typeFilter === 'VIDEO'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              {t('mediapicker_videos')}
            </button>
            <button
              onClick={() => setTypeFilter('PDF')}
              className={cn(
                'px-2 lg:px-3 py-1 lg:py-1.5 rounded-full text-xs lg:text-sm font-medium transition-colors whitespace-nowrap',
                typeFilter === 'PDF'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              PDF
            </button>
          </div>
        )}

        {/* Upload Zone */}
        {showUpload && (
          <MediaUploadZone
            onUploadComplete={handleUploadComplete}
            maxFiles={10}
            className="mb-4"
          />
        )}

        {/* Media Grid */}
        {isLoading ? (
          // Loading Skeleton with shimmer
          <div className="grid grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="aspect-square rounded-xl bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-pulse"
                style={{ animationDelay: `${i * 50}ms` }}
              />
            ))}
          </div>
        ) : isEmpty ? (
          // Empty State
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <i className={`hm hm-${ICONS.IMAGE} text-3xl text-gray-400`} />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {hasFilters ? 'No results found' : 'No media yet'}
            </h3>
            <p className="text-sm text-gray-500 text-center max-w-sm">
              {hasFilters
                ? 'No media found for this type'
                : 'Upload some files to get started with your media library'}
            </p>
            {hasFilters && (
              <button
                onClick={handleClearFilters}
                className="mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium"
              >
                {t('mediapicker_clear_filters')}
              </button>
            )}
          </div>
        ) : (
          // Media Grid
          <div className="grid grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
            {filteredMedia.map((item: MediaLibraryItem) => {
              const isSelected = selectedItems.has(item.id);

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleItemClick(item)}
                  aria-pressed={isSelected}
                  aria-label={`${t('select')}: ${item.filename}`}
                  className={cn(
                    'relative aspect-square rounded-xl overflow-hidden cursor-pointer group border-0 text-left focus:outline-none focus-visible:outline-2 focus-visible:outline-primary-600',
                    'shadow-sm hover:shadow-lg transition-shadow duration-300',
                    isSelected && 'ring-2 ring-primary-500 ring-offset-2'
                  )}
                >
                  {/* Thumbnail - use thumbnailUrl with fallback */}
                  <RetryImage
                    src={item.thumbnailUrl || item.url}
                    alt={item.filename}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />

                  {/* Video play icon overlay */}
                  {item.type === 'VIDEO' && (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                        <i className={`hm hm-${ICONS.PLAY} text-white text-xl ml-1`} />
                      </span>
                    </span>
                  )}

                  {/* PDF badge overlay */}
                  {item.type === 'PDF' && (
                    <span className="absolute top-2 left-2 px-2 py-1 bg-red-500 rounded text-white text-[10px] font-bold uppercase">
                      PDF
                    </span>
                  )}

                  {/* Bottom gradient with filename */}
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-3 pt-8 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity">
                    <span className="block text-white text-xs font-medium truncate">{item.filename}</span>
                    {item.width && item.height && (
                      <span className="block text-white/70 text-[10px]">{item.width} × {item.height}</span>
                    )}
                  </span>

                  {/* Selection checkmark - animated */}
                  <span className={cn(
                    'absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200',
                    isSelected
                      ? 'bg-primary-500 scale-100'
                      : 'bg-white/80 backdrop-blur-sm scale-0 group-hover:scale-100 group-focus-within:scale-100 [@media(hover:none)]:scale-100'
                  )}>
                    <i className={cn(
                      `hm hm-${ICONS.CHECK} text-xs transition-colors`,
                      isSelected ? 'text-white' : 'text-gray-400'
                    )} />
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Load More Button */}
        {hasNextPage && (
          <div className="py-4 flex justify-center">
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              {isFetchingNextPage ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                  Loading...
                </span>
              ) : (
                'Load More'
              )}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};
